import React, { createContext, useContext, useState, useEffect } from "react";
import * as util from "../../app/functions/util";
import useGetProfile from "app/popleads/hooks/useGetProfile";
import useEditProfile from "app/popleads/hooks/useEditProfile";
import useGetProfileNotes from "app/popleads/hooks/useGetProfileNotes";
import useCompanyData from "app/popleads/hooks/useCompanyData";
import { toast } from "react-toastify";
import useOlanakEkle from "app/popleads/hooks/useOlanakEkle";
import moment from "moment";
import useGetUserExtensionActivities from "app/popleads/hooks/useGetUserExtensionActivities";
import useGetTaskLists from "app/popleads/hooks/useGetTaskLists";
import useGetEsListe from "app/popleads/hooks/useGetEsListe";
import useDeletePotential from "app/popleads/hooks/useDeletePotential";
import { DEAL_WON, POPUP_CONTENTS, publicTypes, isCipdCampaign } from "./utils/constants";
import useEditDeal from "app/popleads/hooks/useEditDeal";
import useNotSil from "app/popleads/hooks/useNotSil";
import { isNumber } from "lodash";
import useWebSocket from "app/popleads/hooks/useWebSocket";
import useGetWonDealsByAccountId from "app/popleads/hooks/useGetWonDealsByAccountId";
import useListDealsByProfileId from "app/popleads/hooks/useListDealsByProfileId";
import useGetEtiketListByProfileId from "app/popleads/hooks/useGetEtiketListByProfileId";
import useGetTaskListByProfileId from "app/popleads/hooks/useGetTaskListByProfileId";
import useGetAttachments from "app/popleads/hooks/useGetAttachments";
import useGetTagsForProfile from "app/popleads/hooks/useGetTagsForProfile";
import useIntersectionObserver from "app/popleads/hooks/useIntersectionObserver";
import useUploadDealAttachments from "app/popleads/hooks/useUploadDealAttachments";
import {
    NATIONAL_ID_REQUIRED_MESSAGE,
    getNationalIdNumber,
    isNationalIdRequiredForSelfInvestmentDeal,
} from "app/popleads/app/functions/selfInvestmentNationalId";

const MODALS = {
    DELETE_ATTACHMENT: "deleteAttachment",
    DELETE_TASK: "deleteTask",
    DELETE_DEAL: "deleteDeal",
    UPDATE_PROFILE: "updateProfile",
    DELETE_BASED_ON_STATUS: "deleteBasedOnStatus",
    LINKEDIN_URL_UPDATE: "linkedinUrlUpdate",
    DELETE_PROFILE_TASK: "deleteProfileTask",
    DELETE_BULK_TASKS: "deleteBulkTasks",
};

const ExtensionContext = createContext();

export function useExtensionContext() {
    return useContext(ExtensionContext);
}

export const ExtensionConsumer = ExtensionContext.Consumer;

const initialState = {
    selectedTab: "Profile",
    selectedDeal: null,
    popupContent: "",
    popupIsOpen: false,
    taskPage: null,
    profileId: null,
    loading: false,
    error: null,
    myActivities: [],
    selectedModal: null,
    selectedTask: null,
    editMode: false,
    selectedDate: new Date(),
    fieldToUpdate: null,
    fieldToEdit: null,
    selectedCampaignStartDate: null,
    showMailModal: false,
    mailContent: null,
    isPopitSearch: false,
    modalMessage: "",
    isDealEditing: false,
    selectedTasksId: "",
    updateProfileParams: null,
    isIframeOpen: false,
    isRedirectingToDeal: false,
    showOverdueTasks: false,
    onlyCompletedTasks: false,
    expandedTaskIds: [],
    taskCarouselIndex: 0,
};

const COLLAPSED_CARDS_KEY = "extensionCollapsedCards";
const defaultCollapsedCards = {
    deal: false,
    task: false,
    note: false,
    typeTag: false,
    course: false,
};

function getInitialCollapsedCards() {
    try {
        const stored = localStorage.getItem(COLLAPSED_CARDS_KEY);
        if (stored) {
            return { ...defaultCollapsedCards, ...JSON.parse(stored) };
        }
    } catch (e) {}
    return { ...defaultCollapsedCards };
}

export function ExtensionProvider({ extensionEvents, children }) {
    const queryParams = new URLSearchParams(window.location.search);
    const isGenerateNewLead = ["1", "true"].includes((queryParams.get("generateNewLead") || "").toLowerCase());

    const queryProfileId = parseInt(util.getUrlQueryParam("profileId"));
    const queryDealId = parseInt(util.getUrlQueryParam("dealId"));
    const rawLinkedinUrl = queryParams.get("url")?.replace(/\/$/, "");
    const rawLinkedinProfileUrl = queryParams.get("profil_url")?.replace(/\/$/, "");
    const linkedinUrl = rawLinkedinProfileUrl
        ? rawLinkedinProfileUrl
        : rawLinkedinUrl?.startsWith("https://www.linkedin.com/in/")
        ? rawLinkedinUrl
        : "";
    const profileIdParam = queryProfileId || linkedinUrl;

    const [urlParams, setUrlParams] = useState(null);
    const { deletePotential, loading: deletePotentialLoading } = useDeletePotential();
    const {
        wonDeals,
        loading: wonDealsLoading,
        error: wonDealsError,
        fetchWonDealsByAccountId,
    } = useGetWonDealsByAccountId();

    useEffect(() => {
        const params = {
            profil_ad: util.getUrlQueryParam("profil_ad"),
            profil_baslik: queryParams.get("profil_baslik"),
            profil_headline: util.getUrlQueryParam("profil_headline"),
            profil_email: util.getUrlQueryParam("profil_email"),
            profil_tel: queryParams.get("profil_tel"),
            profil_resim: queryParams.get("profil_resim"),
            profil_ulke: util.getUrlQueryParam("profil_ulke"),
            profil_sirket: util.getUrlQueryParam("profil_sirket"),
            profil_url: util.getUrlQueryParam("profil_url"),
        };
        setUrlParams(params);
    }, []);

    const [isProfileNotFound, setIsProfileNotFound] = useState(
        !isGenerateNewLead && !queryProfileId && queryProfileId !== 0 && (!linkedinUrl || !linkedinUrl.trim())
    );

    const [state, setState] = useState({
        ...initialState,
        selectedTab: isGenerateNewLead ? "New Lead" : "Profile",
    });

    const [profile, setProfile] = useState(null);

    const [noteList, setNoteList] = useState([]);

    const { profile: resProfile, loading, error, refetch } = useGetProfile(profileIdParam, true);

    const [collapsedCards, setCollapsedCards] = useState(getInitialCollapsedCards());

    const [dealAttachments, setDealAttachments] = useState([]);
    // CIPD deals require a dedicated, mandatory CV attachment - tracked separately
    // from the generic "Upload Document" attachments above.
    const [cvAttachment, setCvAttachment] = useState([]);

    const [dealRef] = useIntersectionObserver({
        threshold: 0.1,
        rootMargin: "0px",
        triggerOnce: true,
    });
    const [courseRef] = useIntersectionObserver({
        threshold: 0.1,
        rootMargin: "0px",
        triggerOnce: true,
    });
    const [taskRef] = useIntersectionObserver({
        threshold: 0.1,
        rootMargin: "0px",
        triggerOnce: true,
    });
    const [noteRef] = useIntersectionObserver({
        threshold: 0.1,
        rootMargin: "0px",
        triggerOnce: true,
    });
    const [typeTagRef] = useIntersectionObserver({
        threshold: 0.1,
        rootMargin: "0px",
        triggerOnce: true,
    });

    const shouldFetchDeals = true; // !collapsedCards.deal && hasDealIntersected && profile?.olanak?.length === 0;
    const shouldFetchCourse = true; // !collapsedCards.course && hasCourseIntersected && profile?.etiket?.length === 0;
    const shouldFetchTask = true; //  !collapsedCards.task && hasTaskIntersected && profile?.gorev?.length === 0;
    const shouldFetchNote = true; // !collapsedCards.note && hasNoteIntersected && noteList?.length === 0;
    const shouldFetchTypeTag = true; // !collapsedCards.typeTag && hasTypeTagIntersected && profile?.tag?.length === 0;

    const {
        deals: resDeals,
        loading: dealsLoading,
        error: dealsError,
        refetch: refetchDeals,
    } = useListDealsByProfileId(shouldFetchDeals ? profile?.id : null);
    const {
        etiketList: resEtiketList,
        loading: etiketListLoading,
        error: etiketListError,
        refetch: refetchEtiketList,
    } = useGetEtiketListByProfileId(shouldFetchCourse ? profile?.id : null);
    const {
        taskList: resTaskList,
        loading: getTaskListLoading,
        error: taskListError,
        refetch: refetchTaskList,
    } = useGetTaskListByProfileId(shouldFetchTask ? profile?.id : null);
    const {
        attachments: resAttachments,
        loading: getAttachmentsLoading,
        error: attachmentsError,
        refetch: refetchAttachments,
    } = useGetAttachments(shouldFetchNote ? profile?.id : null);
    const { tags: resTags, loading: getTagsLoading, error: tagsError, refetch: refetchTags } = useGetTagsForProfile(
        shouldFetchTypeTag ? profile?.id : null
    );

    const { profileNotes, loading: profileNotesLoading, refetch: profileNotesRefetch } = useGetProfileNotes(
        shouldFetchNote ? profile?.id : null,
        0,
        10
    );

    const { uploadDealAttachments, uploadDealCv } = useUploadDealAttachments();

    useEffect(() => {
        if (resDeals) {
            const inhouseInquiries = resDeals
                .filter((item) => item.activityType === "In-house Inquiry")
                .map((item) => ({
                    label: item.activityName,
                    value: item.activityId,
                }));

            setProfile((prevState) => {
                return {
                    ...prevState,
                    olanak: resDeals,
                    inhouseInquiries,
                };
            });
        }
    }, [resDeals]);

    useEffect(() => {
        if (resEtiketList) {
            setProfile((prevState) => {
                return {
                    ...prevState,
                    etiket: resEtiketList,
                };
            });
        }
    }, [resEtiketList]);

    useEffect(() => {
        if (resTaskList) {
            setProfile((prevState) => {
                return {
                    ...prevState,
                    gorev: resTaskList,
                };
            });
        }
    }, [resTaskList]);

    useEffect(() => {
        if (resAttachments) {
            setProfile((prevState) => {
                return {
                    ...prevState,
                    attachments: resAttachments,
                };
            });
        }
    }, [resAttachments]);

    useEffect(() => {
        if (resTags) {
            setProfile((prevState) => {
                return {
                    ...prevState,
                    tag: resTags,
                };
            });
        }
    }, [resTags]);

    useEffect(() => {
        if (!loading && resProfile) {
            const { olanak, etiket, gorev, attachments, tag, ...restProfile } = resProfile || {};
            setProfile((prevState) => ({
                ...prevState,
                ...restProfile,
            }));
        }
    }, [resProfile, loading]);

    const { olanakEkle } = useOlanakEkle();
    const { companyUsers, companyGroups, companyUsersAndGroups } = useCompanyData();
    const [popit, setPopit] = useState(false);

    const initialDealObject = {
        campaignType: "",
        status: "",
        dealPotential: 25,
        amount: "",
        numberOfUnits: 1,
        selectedInquiry: null,
        newInquiry: false,
        vatIncludes: "Yes",
        isSelfInvestment: "No",
        nationalIdNumber: "",
        membership: "",
        dateOfBirth: "",
        physicalAddress: "",
        numberOfInstallments: "",
    };

    const [dealObject, setDealObject] = useState(initialDealObject);

    const [selectedCampaignStartDate, setSelectedCampaignStartDate] = useState(null);

    const [newLeadObject, setNewLeadObject] = useState(null);

    const isInhouseTypes = (campaignType) =>
        campaignType === "In-house" || campaignType === "LVT In-House" || campaignType === "Learning Journey";
    const isInquiryTypes = (campaignType) =>
        campaignType === "E-Learning" ||
        campaignType === "EdTech" ||
        campaignType === "Consulting" ||
        campaignType === "Coaching";

    const { userActivities, refetch: fetchUserExtensionActivitiesFunc } = useGetUserExtensionActivities();
    const {
        tasks: taskList,
        loading: taskListLoading,
        refetch: fetchTaskLists,
        staticTasks: staticTaskList,
        staticLoading: staticTaskListLoading,
        fetchStaticTaskList,
    } = useGetTaskLists();
    const { editDeal } = useEditDeal();

    const {
        feed,
        setFeed,
        notes: liveNotes,
        setNotes: setLiveNotes,
        activities: liveActivities,
        setActivities: setLiveActivities,
        tasks: liveTasks,
        setTasks: setLiveTasks,
        profileTasks: liveProfileTasks,
        setProfileTasks: setLiveProfileTasks,
        deals: liveDeals,
        setDeals: setLiveDeals,
        updateDealWithInhouseCampaign,
        setUpdateDealWithInhouseCampaign,
        deletedPotential,
        setDeletedPotential,
    } = useWebSocket();

    useEffect(() => {
        if (profile && liveProfileTasks.length > 0) {
            const filteredTasks = liveProfileTasks.filter((task) => task?.profilId === profile?.id);
            if (filteredTasks.length > 0) {
                setProfile((prevState) => ({
                    ...prevState,
                    gorev: [...prevState.gorev, ...filteredTasks].sort((a, b) => new Date(a.zaman) - new Date(b.zaman)),
                }));
            }
            setLiveProfileTasks([]);
        }
    }, [profile, liveProfileTasks]);

    useEffect(() => {
        if (profile && liveDeals.length > 0) {
            const filteredDeals = liveDeals.filter((deal) => deal?.profilId === profile?.id);
            if (filteredDeals.length > 0) {
                setProfile((prevState) => ({
                    ...prevState,
                    olanak: [...filteredDeals, ...prevState.olanak],
                }));
            }
            setLiveDeals([]);
        }
    }, [profile, liveDeals]);

    useEffect(() => {
        if (profile && queryDealId) {
            const deal = profile.olanak.find((deal) => deal.id === queryDealId);
            if (deal) {
                //sadece dealID'yi urlden temizle, diğer parametreleri koru
                const url = new URL(window.location.href);
                url.searchParams.delete("dealId");
                window.history.replaceState({}, "", url.toString());

                //deali göster
                setState((prevState) => ({
                    ...prevState,
                    selectedDeal: deal,
                    popupContent: POPUP_CONTENTS.DEALS,
                    popupIsOpen: true,
                }));
            }
        }
    }, [profile, queryDealId]);

    useEffect(() => {
        if (profile && updateDealWithInhouseCampaign.length > 0) {
            const filteredDeals = updateDealWithInhouseCampaign.filter((deal) => deal?.profilId === profile?.id);
            if (filteredDeals.length > 0) {
                setProfile((prevState) => ({
                    ...prevState,
                    olanak: prevState?.olanak?.map((existingDeal) => {
                        const matchingDeal = filteredDeals.find((deal) => deal.id === existingDeal.id);
                        return matchingDeal ? { ...existingDeal, ...matchingDeal } : existingDeal;
                    }),
                }));
            }
            setUpdateDealWithInhouseCampaign([]);
        }
    }, [profile, updateDealWithInhouseCampaign]);

    useEffect(() => {
        if (profile && deletedPotential.length > 0) {
            const filteredDeals = deletedPotential.filter((deal) => deal?.profilId === profile?.id);
            if (filteredDeals.length > 0 && !deletePotentialLoading) {
                const hasMatch = profile?.olanak?.some((existingDeal) =>
                    filteredDeals.some((deal) => deal.id === existingDeal.id)
                );
                if (hasMatch) {
                    setProfile((prevState) => ({
                        ...prevState,
                        olanak: prevState?.olanak?.filter(
                            (existingDeal) => !filteredDeals.some((deal) => deal.id === existingDeal.id)
                        ),
                    }));
                }
            }
            setDeletedPotential([]);
        }
    }, [profile, deletedPotential, deletePotentialLoading]);

    const { notSil, loading: isDeletingNote } = useNotSil(profile?.id);

    const handleDeleteNote = (noteId) => {
        notSil({ noteId }).then(() => {
            profileNotesRefetch({ rowsPerPage: 10, start: 0 });
            refetchAttachments();
        });
        //refetch();
    };

    const { updateProfile, loading: updateProfileLoading, error: updateProfileError } = useEditProfile();

    useEffect(() => {
        if (
            profile &&
            urlParams.profil_resim &&
            !urlParams?.profil_resim.startsWith("data:image") &&
            profile.resim !== urlParams.profil_resim
        ) {
            // Update profile image if it exists in URL params and is different from current profile image
            updateProfile({
                id: profile.id,
                field: "resim",
                fieldValue: urlParams.profil_resim,
            });
        }
    }, [profile, urlParams]);

    const handlePopit = (profileId, url) => {
        if (url) {
            toast.success("Created new lead successfully");
            refetch();
        } else {
            setState({
                ...initialState,
                selectedTab: "Feed",
            });
            window.open(`/extension?profileId=${profileId}`, "_blank");
        }
    };

    const { getEsListe, profilListe, loading: profilListeLoading } = useGetEsListe(handlePopit);

    const [olanakEkleLoading, setOlanakEkleLoading] = useState(false);

    const handleUpdateProfile = async (params = {}, autoRefech = true) => {
        const result = await updateProfile({ id: profile.id, ...params });
        setState((prevState) => ({
            ...prevState,
            updateProfileParams: null,
        }));
        if (result.error) return result;

        if (result.data.mergeProfile) {
            toast.warning(
                `We have reached to the same url in this profile '${result.data.mergeProfile}' you can merge the profiles if you want`
            );
            return;
        }

        if (autoRefech) {
            refetch();
            // profileNotesRefetch();
        }
    };

    // CIPD deals (Public/LVT Public campaigns whose name contains "CIPD") require
    // Date of Birth, Physical Address and a CV attachment always, and Number of
    // Installments only when Self Investment is selected.
    const validateCipdRequiredFields = (dealObj) => {
        if (!dealObj.dateOfBirth) {
            toast.error("Please enter date of birth");
            return false;
        }
        if (
            moment(dealObj.dateOfBirth)
                .startOf("day")
                .isAfter(moment().startOf("day"))
        ) {
            toast.error("Date of birth can not be a future date");
            return false;
        }
        if (!dealObj.physicalAddress || dealObj.physicalAddress.trim() === "") {
            toast.error("Please enter physical address");
            return false;
        }
        if (!cvAttachment?.length && !dealObj.cvAttachment) {
            toast.error("Please upload a CV attachment");
            return false;
        }
        if (dealObj.isSelfInvestment === "Yes" || parseInt(dealObj.isSelfInvestment) === 1) {
            const installments = Number(dealObj.numberOfInstallments);
            if (!dealObj.numberOfInstallments || !Number.isInteger(installments) || installments <= 0) {
                toast.error("Please enter a valid number of installments");
                return false;
            }
        }
        return true;
    };

    const getAttachmentId = (attachment) => attachment?.id || attachment?.attachmentId || attachment?.fileId;

    const getUploadedCvAttachmentId = (data) =>
        data?.cvAttachmentId ||
        data?.attachmentId ||
        getAttachmentId(data?.cvAttachment) ||
        getAttachmentId(data?.attachment) ||
        getAttachmentId(data?.attachments?.[0]) ||
        data?.id;

    const uploadDealFilesForActivity = async (dealObj) => {
        let attachments = [];
        let cvAttachmentId = dealObj.cvAttachmentId || getAttachmentId(dealObj.cvAttachment);

        if (dealAttachments?.length > 0) {
            const res = await uploadDealAttachments({ files: dealAttachments });
            if (res.error) {
                return { error: res.error };
            }
            attachments = res?.data?.attachments || [];
        }

        if (isCipdCampaign(dealObj.campaign) && cvAttachment?.length > 0) {
            const res = await uploadDealCv({ files: cvAttachment });
            if (res.error) {
                return { error: res.error };
            }
            cvAttachmentId = getUploadedCvAttachmentId(res?.data);
            if (!cvAttachmentId) {
                return { error: "CV attachment could not be uploaded." };
            }
        }

        return { attachments, cvAttachmentId };
    };

    const validateDealObject = (dealObj) => {
        if (!dealObj.dealCampaignType) {
            toast.error("Campaign type is required.");
            return false;
        }

        if (!dealObject.amount) {
            toast.error("You need to enter the amount first");
            return;
        }
        if (dealObject.status === DEAL_WON && !dealObject.closingDate) {
            toast.error("Please choose a closing date");
            return;
        }
        if (
            isNationalIdRequiredForSelfInvestmentDeal({
                activityType: "Invoice Requisition",
                dealCampaignType: dealObj.dealCampaignType,
                isSelfInvestment: dealObj.isSelfInvestment,
                status: dealObj.status,
            }) &&
            !getNationalIdNumber(dealObj)
        ) {
            toast.error(NATIONAL_ID_REQUIRED_MESSAGE);
            return false;
        }
        const isCipdDeal = Boolean(Number(dealObj.campaign?.isCipd));
        if (isCipdDeal) {
            const dealStatus = dealObj.status || dealObj.durum;
            if (dealStatus === DEAL_WON && !validateCipdRequiredFields(dealObj)) {
                return false;
            }
            if (dealStatus === DEAL_WON && !String(dealObj.membership || "").trim()) {
                toast.error("Membership is required.");
                return false;
            }
        }
        if (
            dealObject?.campaign?.type === "Public-ExEd" &&
            parseFloat(dealObject.amount) > parseFloat(dealObject.campaign.fullPrice)
        ) {
            toast.error("Deal amount can not be higher than campaign full price!");
            return false;
        }
        if (
            dealObject.paidAmount &&
            parseInt(dealObject.paidAmount) > parseFloat(dealObject.amount) * dealObject.numberOfUnits
        ) {
            toast.error("Paid amount cannot be greater than the deal amount");
            return false;
        }

        if (isInhouseTypes(dealObj.dealCampaignType) || isInquiryTypes(dealObj.dealCampaignType)) {
            // if (!dealObj.selectedInquiry) {
            //     toast.error("Inquiry is required.");
            //     return false;
            // }

            if ((!profile.sirket || !profile.accountName) && !dealObj.accountName && !dealObj.selectedInquiry) {
                toast.error("You need to enter the company info for the profile.");
                return false;
            }

            if (dealObj.selectedInquiry && !dealObj.accountName) {
                toast.error("You need to enter an account name.");
                return false;
            }

            if (dealObj.courseName === "newCourseName" && !dealObj.newCourseName) {
                switch (dealObj.dealCampaignType) {
                    case "E-Learning":
                    case "EdTech":
                        toast.error("You need to enter new e-learning bundles.");
                        break;
                    case "Consulting":
                        toast.error("You need to enter new consulting type.");
                        break;
                    case "Coaching":
                        toast.error("You need to enter new coaching type.");
                        break;
                    default:
                        toast.error("You need to enter new course name.");
                        break;
                }
                return false;
            }

            if (
                dealObj.status === DEAL_WON &&
                (dealObj.type === "Marketing" || dealObj.type === "Marketing Cross Sale") &&
                !dealObj.channelType
            ) {
                toast.error("Please select marketing channel");
                return false;
            }

            if (dealObject.dealCampaignType === "L&D Broker" && !dealObject.vendorPaymentAmount) {
                toast.error("Please enter amount for vendor payment");
                return;
            }
            if (dealObject.dealCampaignType === "L&D Broker" && !dealObject.leoronMarginAmount) {
                toast.error("Please enter margin amount for LEORON");
                return;
            }

            if (dealObject.type === "Re-Seller Partnership" && dealObject?.endUser?.length > 150) {
                toast.error("End User must be at most 150 characters");
                return;
            }

            if (dealObj.status === DEAL_WON) {
                if (
                    !dealObj.courseName &&
                    (isInhouseTypes(dealObj.dealCampaignType) || isInquiryTypes(dealObj.dealCampaignType))
                ) {
                    if (dealObj.toBe === DEAL_WON) {
                        switch (dealObj.dealCampaignType) {
                            case "E-Learning":
                            case "EdTech":
                                toast.error("You need to select an e-learning bundles from the list.");
                                break;
                            case "Consulting":
                                toast.error("You need to select a consulting type from the list.");
                                break;
                            case "Coaching":
                                toast.error("You need to select a coaching type from the list.");
                                break;
                            default:
                                toast.error("You need to select a course name from the list.");
                                break;
                        }
                        return false;
                    } else {
                        if (isInhouseTypes(dealObj.dealCampaignType)) {
                            toast.error("You need to enter a course name.");
                            return false;
                        }
                    }
                }

                if (!dealObject.type) {
                    toast.error("You need to select a type.");
                    return false;
                }
            }
        }
        return true;
    };

    const createInhouseDeal = async (dealObj) => {
        if (!dealObj.profilId && profile.id) {
            dealObj.profilId = profile.id;
        }

        if (!dealObj.activityId || dealObj.createAnotherDeal) {
            //deal oluşturulmamış. activity oluşturulacak.
            if (
                dealObj.dealCampaignType === "In-house" ||
                dealObj.dealCampaignType === "LVT In-House" ||
                dealObj.dealCampaignType === "Learning Journey"
            ) {
                if (!dealObj.selectedInquiry && !dealObj.createAnotherDeal) {
                    setState((prevState) => ({
                        ...prevState,
                        isRedirectingToDeal: true,
                    }));
                    if (dealAttachments?.length > 0 && dealObj.status === DEAL_WON) {
                        const res = await uploadDealAttachments({ files: dealAttachments });
                        if (res.error) {
                            toast.error(res.error);
                            return;
                        }
                        const attachments = res?.data?.attachments || [];
                        setDealAttachments([]);
                        const activityURL = await createInHouseActivityURL(profile.id, dealObj, attachments);
                        window.open(activityURL, "_self");
                    } else {
                        const activityURL = await createInHouseActivityURL(profile.id, dealObj);
                        window.open(activityURL, "_self");
                    }
                } else {
                    const courseName =
                        dealObj.courseName === "newCourseName" ? dealObj.newCourseName : dealObj.courseName || "";
                    const newCourseNameForInhouse = dealObj.courseName === "newCourseName" ? 1 : 0;

                    const result = await olanakEkle({
                        fn: "olanakEkle",
                        profil: profile.id,
                        ad: dealObj.ad || profile.ad + " " + profile.soyad || "",
                        units: dealObject.numberOfUnits || 1,
                        dealCampaignType: dealObj.dealCampaignType,
                        kapanmaTarihi: dealObj.closingDate,
                        accountName: dealObj.accountName || profile.accountName || "",
                        amount: dealObj.amount || "1",
                        paidAmount: dealObject.paid === "Yes" ? dealObj.amount : null,
                        durum: dealObj.status || `${dealObj.dealPotential}%`,
                        lastDurum: `${dealObj.dealPotential}%`,
                        type: dealObj.type || "",
                        nationalIdNumber: dealObj.nationalIdNumber || "",
                        membership: dealObj.membership || "",
                        paid: dealObject.paid === "Yes" ? 1 : 0,
                        inquiryActivityId: dealObj?.selectedInquiry?.activityId || dealObj.activityId,
                        typeChannel: dealObj.channelType || "",
                        courseName: courseName,
                        newCourseNameForInhouse: newCourseNameForInhouse,
                        partnerUserId: dealObj.partnerUserId || null,
                        unitId: dealObj.unitId || null,
                        vatIncludes: dealObj.vatIncludes === "Yes" || parseInt(dealObj.vatIncludes) === 1 ? 1 : 0,
                        vendorPaymentAmount:
                            dealObj.dealCampaignType === "L&D Broker" ? dealObj.vendorPaymentAmount : "",
                        leoronMarginAmount: dealObj.dealCampaignType === "L&D Broker" ? dealObj.leoronMarginAmount : "",
                        endUser: dealObj.type === "Re-Seller Partnership" ? dealObj.endUser : "",
                    });
                    if (result?.status === 200) {
                        //await refetch();
                        await refetchDeals();
                        setState({
                            ...state,
                            popupContent: POPUP_CONTENTS.DEALS,
                            selectedDeal: result.data.profil.olanak[0],
                            popupIsOpen: true,
                        });
                        toast.success("Deal created successfully.");
                        return true;
                    } else {
                        toast.error(result.response?.data?.text || "Deal could not be created.");
                        return false;
                    }
                }

                return;
            } else if (
                dealObj.dealCampaignType === "EdTech" ||
                dealObj.dealCampaignType === "Consulting" ||
                dealObj.dealCampaignType === "E-Learning" ||
                dealObj.dealCampaignType === "Coaching"
            ) {
                setState((prevState) => ({
                    ...prevState,
                    isRedirectingToDeal: true,
                }));

                if (dealAttachments?.length > 0 && dealObj.status === DEAL_WON) {
                    const res = await uploadDealAttachments({ files: dealAttachments });
                    if (res.error) {
                        toast.error(res.error);
                        return;
                    }
                    const attachments = res?.data?.attachments || [];
                    setDealAttachments([]);
                    const activityURL = await createElearningInquiryURL(profile.id, dealObj, attachments);
                    window.open(activityURL, "_self");
                } else {
                    const activityURL = await createElearningInquiryURL(profile.id, dealObj);
                    window.open(activityURL, "_self");
                }
                return;
            }
        } else if (dealObj.activityId) {
            //deal oluşturulmuş. activity güncellenecek.
            try {
                await editDeal(dealObj);
                //await refetch();
                await refetchDeals();
                setState({ ...state, popupContent: POPUP_CONTENTS.DEALS, popupIsOpen: true });

                return;
            } catch (err) {
                toast.error(err);
                return;
            }
        }
    };

    const handleCreateDeal = async () => {
        setOlanakEkleLoading(true);
        try {
            if (
                (dealObject.partner === null || dealObject.partner === "") &&
                dealObject.partnerName !== null &&
                dealObject.partnerName !== ""
            ) {
                dealObject.partner = companyUsers.find((user) => user.name === dealObject.partnerName)?.id;
            }

            if (!validateDealObject(dealObject)) {
                return;
            }

            const campaignType = dealObject.dealCampaignType;

            if (publicTypes.includes(campaignType) && !dealObject.campaign) {
                toast.error("You need to select a campaign first");
                return;
            }

            if (!dealObject.amount) {
                toast.error("You need to enter the amount first");
                return;
            }
            if (dealObject.amount > 99999999) {
                toast.error("Amount cannot be more than 99,999,999");
                return false;
            }
            if (
                dealObject?.campaign?.type === "Public-ExEd" &&
                parseFloat(dealObject.amount) > parseFloat(dealObject.campaign.fullPrice)
            ) {
                toast.error("Deal amount can not be higher than campaign full price!");
                return false;
            }

            if (dealObject.status === DEAL_WON) {
                if (!dealObject.closingDate) {
                    toast.error("Closing date can not be empty");
                    return;
                }

                if (moment(dealObject?.closingDate).year() !== moment(dealObject?.campaign?.basTarih).year()) {
                    toast.error("Your closing date year must be the same as the campaign year");
                    return;
                }

                if (!dealObject.type) {
                    toast.error("You need to enter the type first");
                    return;
                }
                if (
                    (dealObject.type === "Marketing" || dealObject.type === "Marketing Cross Sale") &&
                    !dealObject.channelType
                ) {
                    toast.error("Please select marketing channel");
                    return;
                }
                if (dealObject.dealCampaignType === "L&D Broker" && !dealObject.vendorPaymentAmount) {
                    toast.error("Please enter amount for vendor payment");
                    return;
                }
                if (dealObject.dealCampaignType === "L&D Broker" && !dealObject.leoronMarginAmount) {
                    toast.error("Please enter margin amount for LEORON");
                    return;
                }
                if (dealObject.type === "Re-Seller Partnership" && dealObject?.endUser?.length > 150) {
                    toast.error("End User must be at most 150 characters");
                    return;
                }
                // Public ve LVT Public tipli Won deallarda da certificate kontrolleri
                if (publicTypes.includes(campaignType)) {
                    if (!dealObject.certificateName || dealObject.certificateName.trim() === "") {
                        toast.error("Please provide a certificate name");
                        return;
                    }
                    if (!dealObject.certificateLastName || dealObject.certificateLastName.trim() === "") {
                        toast.error("Please provide a certificate lastname");
                        return;
                    }
                }
            }

            if (["Lost", "Cancelled"].includes(dealObject.status) && !dealObject.closingDate) {
                toast.error("Please choose a Closing Date");
                return;
            }
            if (
                (dealObject.type === "Marketing" || dealObject.type === "Marketing Cross Sale") &&
                !dealObject.channelType
            ) {
                toast.error("Please select marketing channel");
                return;
            }
            if (dealObject.dealCampaignType === "L&D Broker" && !dealObject.vendorPaymentAmount) {
                toast.error("Please enter amount for vendor payment");
                return;
            }
            if (dealObject.dealCampaignType === "L&D Broker" && !dealObject.leoronMarginAmount) {
                toast.error("Please enter margin amount for LEORON");
                return;
            }
            if (dealObject.type === "Re-Seller Partnership" && dealObject?.endUser?.length > 150) {
                toast.error("End User must be at most 150 characters");
                return;
            }
            if (
                campaignType === "Public-ExEd" &&
                parseFloat(dealObject?.campaign?.fullPrice) < parseFloat(dealObject.amount)
            ) {
                toast.error("Deal amount can not be higer than campaign full price!");
                return;
            }

            if (publicTypes.includes(campaignType)) {
                if (
                    dealObject?.status === DEAL_WON &&
                    (dealObject?.dealCampaignType === "Public - Certified Programs" ||
                        dealObject?.dealCampaignType === "Public - Short Courses" ||
                        dealObject?.dealCampaignType === "LVT Public - Certified Programs" ||
                        dealObject?.dealCampaignType === "LVT Public - Short Courses" ||
                        dealObject?.dealCampaignType === "Public-ExEd" ||
                        dealObject?.dealCampaignType === "Self-Paced" ||
                        dealObject?.dealCampaignType === "L&D Broker")
                ) {
                    setState((prevState) => ({
                        ...prevState,
                        isRedirectingToDeal: true,
                    }));
                    if ((dealAttachments?.length > 0 || cvAttachment?.length > 0) && dealObject.status === DEAL_WON) {
                        const { attachments, cvAttachmentId, error } = await uploadDealFilesForActivity(dealObject);
                        if (error) {
                            toast.error(error);
                            return;
                        }
                        setDealAttachments([]);
                        setCvAttachment([]);
                        const activityURL = createActivityURL(profile.id, dealObject, attachments, cvAttachmentId);
                        window.open(activityURL, "_self");
                        return;
                    } else {
                        const activityURL = createActivityURL(
                            profile.id,
                            dealObject,
                            [],
                            dealObject.cvAttachmentId || getAttachmentId(dealObject.cvAttachment)
                        );
                        window.open(activityURL, "_self");
                        return;
                    }
                } else if (
                    dealObject.id &&
                    dealObject.toBe === DEAL_WON &&
                    dealObject?.dealCampaignType !== "Public - Certified Programs" &&
                    dealObject?.dealCampaignType !== "Public - Short Courses" &&
                    dealObject?.dealCampaignType !== "LVT Public - Certified Programs" &&
                    dealObject?.dealCampaignType !== "LVT Public - Short Courses" &&
                    dealObject?.dealCampaignType !== "Public-ExEd" &&
                    dealObject?.dealCampaignType !== "Self-Paced" &&
                    dealObject?.dealCampaignType !== "L&D Broker"
                ) {
                    try {
                        await editDeal(dealObject);
                        //await refetch();
                        await refetchDeals();
                        setState({ ...state, popupContent: POPUP_CONTENTS.DEALS, popupIsOpen: true });
                        return;
                    } catch (err) {
                        toast.error(err);
                        return;
                    }
                }

                const courseName =
                    dealObject.courseName === "newCourseName" ? dealObject.newCourseName : dealObject.courseName || "";
                const newCourseNameForInhouse = dealObject.courseName === "newCourseName" ? 1 : 0;

                const dealParams = {
                    fn: "olanakEkle",
                    profil: profile.id,
                    ad: profile.ad + " " + profile.soyad || "",
                    accountName: profile.accountName || "",
                    amount: dealObject.amount || "",
                    units: dealObject.numberOfUnits || 1,
                    durum: dealObject.status || `${dealObject.dealPotential}%`,
                    lastDurum: `${dealObject.dealPotential}%`,
                    type: dealObject.type || "",
                    certificateName: dealObject.certificateName || "",
                    certificateLastName: dealObject.certificateLastName || "",
                    nationalIdNumber: dealObject.nationalIdNumber || "",
                    membership: dealObject.membership || "",
                    typeChannel: dealObject.channelType || "",
                    dealCampaignType: dealObject.dealCampaignType || "",
                    isSelfInvestment: dealObject.isSelfInvestment === "Yes" ? 1 : 0,
                    partnerUserId: dealObject.partner || "",
                    vatIncludes: dealObject.vatIncludes === "Yes" ? 1 : 0,
                    kapanmaTarihi: dealObject.closingDate
                        ? isNumber(dealObject.closingDate)
                            ? dealObject.closingDate
                            : moment(dealObject.closingDate)
                                  .startOf("day")
                                  .valueOf()
                        : "",
                    paidAmount: dealObject.paidAmount || "",
                    etiketId: dealObject.campaign.id || null,
                    paid: dealObject.paidDeal === "Yes" ? 1 : 0,
                    courseName: courseName,
                    newCourseNameForInhouse: newCourseNameForInhouse,
                    unitId: dealObject.unitId || null,
                    vendorPaymentAmount:
                        dealObject.dealCampaignType === "L&D Broker" ? dealObject.vendorPaymentAmount : "",
                    leoronMarginAmount:
                        dealObject.dealCampaignType === "L&D Broker" ? dealObject.leoronMarginAmount : "",
                    endUser: dealObject.type === "Re-Seller Partnership" ? dealObject.endUser : "",
                };

                const result = await olanakEkle(dealParams);

                if (result.response?.status === 500 || result.response?.status === 400) {
                    toast.error(result.response?.data?.text || "Deal could not be created.");
                    return;
                }
                const deal = result.data.profil.olanak[0];
                setState({ ...state, popupContent: POPUP_CONTENTS.DEALS, selectedDeal: deal, popupIsOpen: true });

                toast.success("Deal created successfully.");
                setDealObject(initialDealObject);
                //refetch();
                await refetchDeals();
                return;
            }

            const params = new URLSearchParams({
                profileId: profile.id,
                paidAmount: dealObject.paidAmount || "",
                amount: dealObject.amount || "",
                ad: dealObject.dealName || "",
                accountId: profile.accountId || "",
                accountName: dealObject.accountName || "",
                units: dealObject.numberOfUnits || 1,
                campaignId: dealObject.campaign?.id || "",
                dealType: dealObject.type || "",
                certificateName: dealObject.certificateName || "",
                certificateLastName: dealObject.certificateLastName || "",
                nationalIdNumber: dealObject.nationalIdNumber || "",
                membership: dealObject.membership || "",
                channelType: dealObject.channelType || "",
                lastDurum: dealObject.dealPotential || 75,
                dealCampaignType: dealObject.dealCampaignType || "",
                isSelfInvestment: dealObject.isSelfInvestment === "Yes" ? 1 : 0,
                partnerUserId: dealObject.partner || null,
                vatIncludes: dealObject.vatIncludes === "Yes" ? 1 : 0,
                closedTime: dealObject.closingDate ? new Date(dealObject.closingDate).getTime() : "",
                activityType: "Invoice Requisition",
                isFromExtension: 1,
                unitId: dealObject.unitId || null,
                vendorPaymentAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.vendorPaymentAmount : "",
                leoronMarginAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.leoronMarginAmount : "",
                endUser: dealObject.type === "Re-Seller Partnership" ? dealObject.endUser : "",
            });

            setState((prevState) => ({
                ...prevState,
                isRedirectingToDeal: true,
            }));
            window.location.href = `/activities?${params.toString()}`;
        } catch (error) {
            toast.error(error.message);
        } finally {
            setOlanakEkleLoading(false);
        }
    };

    const handleDeleteDeal = async (dealId) => {
        try {
            if (dealObject?.invoiceRequisitionId) {
                toast.error("Deal cannot be removed due to invoice created");
            } else {
                const res = await deletePotential(dealId);
                if (res.data.text === "Success") {
                    //await refetch();  refetchDeals
                    await refetchDeals();
                    if (profile?.olanak?.length > 1) {
                        const lastDeal = profile?.olanak[0];
                        setSelectedDeal(lastDeal || null);
                    } else {
                        setSelectedDeal(null);
                    }
                }
            }
        } catch (err) {
            //console.error(err);
        } finally {
            setState((prevState) => ({
                ...prevState,
                selectedModal: null,
            }));
        }
    };

    const handleCreateInquiry = async () => {
        setOlanakEkleLoading(true);
        try {
            if (!validateDealObject(dealObject)) {
                return;
            }

            if (
                isInhouseTypes(dealObject.dealCampaignType) ||
                dealObject.dealCampaignType === "EdTech" ||
                dealObject.dealCampaignType === "Consulting" ||
                dealObject.dealCampaignType === "E-Learning" ||
                dealObject.dealCampaignType === "Coaching"
            ) {
                if (!(await createInhouseDeal(dealObject))) {
                    return;
                }
            }

            toast.success("Inquiry created successfully.");
        } catch (error) {
            toast.error(error.message);
        } finally {
            setOlanakEkleLoading(false);
        }
    };

    const createActivityURL = (profileId, dealObject, attachments = [], cvAttachmentId = null) => {
        const baseObj = {
            profileId: profileId,
            campaignId: dealObject.campaign?.id || null,
            certificateName: dealObject.certificateName,
            certificateLastName: dealObject.certificateLastName,
            nationalIdNumber: dealObject.nationalIdNumber || "",
            membership: dealObject.membership || "",
            isSelfInvestment: dealObject.isSelfInvestment === "Yes" ? 1 : 0,
            activityType: "Invoice Requisition",
            partnerUserId: dealObject.partner || null,
            dealType: dealObject.type,
            closingDate: isNumber(dealObject.closingDate)
                ? dealObject.closingDate
                : moment(dealObject.closingDate)
                      .startOf("day")
                      .valueOf(),
            dealCampaignType: dealObject.dealCampaignType,
            isFromExtension: 1,
            unitId: dealObject.unitId || null,
        };

        if (dealObject.channelType) {
            baseObj.channelType = dealObject.channelType;
        }

        if (dealObject.dealCampaignType === "L&D Broker" && dealObject.vendorPaymentAmount) {
            baseObj.vendorPaymentAmount = dealObject.vendorPaymentAmount;
        }

        if (dealObject.dealCampaignType === "L&D Broker" && dealObject.leoronMarginAmount) {
            baseObj.leoronMarginAmount = dealObject.leoronMarginAmount;
        }

        if (dealObject.type === "Re-Seller Partnership" && dealObject.endUser) {
            baseObj.endUser = dealObject.endUser;
        }

        if (isCipdCampaign(dealObject.campaign)) {
            baseObj.dateOfBirth = isNumber(dealObject.dateOfBirth)
                ? dealObject.dateOfBirth
                : moment(dealObject.dateOfBirth)
                      .startOf("day")
                      .valueOf();
            baseObj.physicalAddress = dealObject.physicalAddress || "";
            if (cvAttachmentId) {
                baseObj.cvAttachmentId = cvAttachmentId;
            }
            if (dealObject.isSelfInvestment === "Yes") {
                baseObj.numberOfInstallments = dealObject.numberOfInstallments || "";
            }
        }

        // Eğer deal zaten oluşturulmuşsa (dealId varsa)
        if (dealObject.id && !dealObject.createAnotherDeal) {
            const params = new URLSearchParams({
                ...baseObj,
                dealId: dealObject.id,
                dealUnits: dealObject.numberOfUnits || dealObject.units || 1,
                amount: dealObject.amount || "",
                //unitId: dealObject.numberOfUnits, //hatalı.. formdan seçtirmeliyiz..
            });
            return `/activities?${encodeURIComponent(params.toString())}`;
        }
        // Yeni won deal oluşturuluyorsa
        else {
            const wonParams = new URLSearchParams({
                ...baseObj,
                paidAmount: dealObject.paidAmount || "",
                amount: dealObject.amount || "",
                ad: profile.ad + " " + profile.soyad || "",
                accountId: profile.accountId || "",
                accountName: profile.accountName || "",
                units: dealObject.numberOfUnits || 1,
                lastDurum: dealObject.dealPotential || 75,
                vatIncludes: dealObject.vatIncludes === "Yes" ? 1 : 0,
                closedTime: isNumber(dealObject.closingDate)
                    ? dealObject.closingDate
                    : moment(dealObject.closingDate)
                          .startOf("day")
                          .valueOf(),
                unitId: dealObject.unitId || null,
            });
            if (attachments?.length > 0) {
                const attachmentIds = attachments.map((attachment) => attachment.id);
                wonParams.append("attachmentIds", attachmentIds.join(","));
            }
            return `/activities?${encodeURIComponent(wonParams.toString())}`;
        }
    };

    const createInHouseActivityURL = (profileId, dealObject, attachments = []) => {
        const baseObj = {
            profileId: profileId,
            activityType: "In-house Inquiry",
            courseName: dealObject.courseName || "null",
            dealCampaignType: dealObject.dealCampaignType,
            dealType: dealObject.type || "null",
            channelType: dealObject.channelType || "null",
            closedTime: dealObject.closingDate ? new Date(dealObject.closingDate).getTime() : "null",
            amount: dealObject.amount || 0,
            paidAmount: dealObject.paidAmount || 0,
            dealStatus: dealObject.status || dealObject.dealPotential || 25,
            lastDurum: dealObject.dealPotential || 25,
            vatIncludes: dealObject.vatIncludes === "Yes" ? 1 : 0,
            partnerUserId: dealObject.partner || "null",
            isFromExtension: 1,
            unitId: dealObject.unitId || null,
            vendorPaymentAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.vendorPaymentAmount : "",
            leoronMarginAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.leoronMarginAmount : "",
            endUser: dealObject.type === "Re-Seller Partnership" ? dealObject.endUser : "",
        };
        if (attachments?.length > 0) {
            const attachmentIds = attachments.map((attachment) => attachment.id);
            baseObj.attachmentIds = attachmentIds;
        }
        const params = new URLSearchParams(baseObj);
        return `/activities?${params.toString()}`;
    };

    const createRegisterInHouseActivityURL = (profileId, dealObject) => {
        const baseObj = {
            profileId: profileId,
            campaignId: "null",
            dealId: dealObject.id || "null",
            dealUnits: dealObject.numberOfUnits || 1,
            certificateName: "null",
            certificateLastName: "null",
            unitId: dealObject.unitId || "null",
            partnerUserId: dealObject.partner || companyUsers.find((user) => user.name === dealObject.partnerName)?.id,
            dealType: dealObject.type || "null",
            dealChannelType: dealObject.channelType || "null",
            closingDate: dealObject.kapanmaTarihi
                ? dealObject.kapanmaTarihi
                : dealObject.closingDate
                ? new Date(dealObject.closingDate).getTime()
                : "null",
            dealCampaignType: dealObject.dealCampaignType,
            activityType: "In-house Confirmation",
            courseName: dealObject.courseName || "null",
            isFromExtension: 1,
            vendorPaymentAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.vendorPaymentAmount : "",
            leoronMarginAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.leoronMarginAmount : "",
            endUser: dealObject.type === "Re-Seller Partnership" ? dealObject.endUser : "",
        };

        const params = new URLSearchParams(baseObj);
        return `/activities?${params.toString()}`;
    };

    const createElearningInquiryURL = (profileId, dealObject, attachments = []) => {
        const baseObj = {
            profileId: profileId,
            courseName: dealObject.courseName || "null",
            dealCampaignType: dealObject.dealCampaignType || "null",
            dealType: dealObject.type || "null",
            channelType: dealObject.channelType || "null",
            closedTime: dealObject.closingDate ? new Date(dealObject.closingDate).getTime() : "null",
            amount: dealObject.amount || 1,
            paidAmount: dealObject.paidAmount || 0,
            dealStatus: dealObject.status || dealObject.dealPotential || 25,
            lastDurum: dealObject.dealPotential || 25,
            vatIncludes: 1,
            partnerUserId: dealObject.partnerUserId || dealObject.partner || "null",
            isFromExtension: 1,
            unitId: dealObject.unitId || null,
            vendorPaymentAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.vendorPaymentAmount : "",
            leoronMarginAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.leoronMarginAmount : "",
            endUser: dealObject.type === "Re-Seller Partnership" ? dealObject.endUser : "",
        };

        if (dealObject.dealCampaignType === "E-Learning" || dealObject.dealCampaignType === "EdTech") {
            baseObj.activityType = "eLearning Inquiry";
        } else if (dealObject.dealCampaignType === "Consulting") {
            baseObj.activityType = "Consulting Services";
        } else if (dealObject.dealCampaignType === "Coaching") {
            baseObj.activityType = "Coaching Inquiry";
        }

        if (attachments?.length > 0) {
            const attachmentIds = attachments.map((attachment) => attachment.id);
            baseObj.attachmentIds = attachmentIds;
        }

        const params = new URLSearchParams(baseObj);
        return `/activities?${params.toString()}`;
    };

    const createRegisterELearningActivityURL = (profileId, dealObject, activityType) => {
        const baseObj = {
            profileId: profileId,
            campaignId: "null",
            dealId: dealObject.id || "null",
            dealUnits: dealObject.numberOfUnits || 1,
            certificateName: "null",
            certificateLastName: "null",
            unitId: dealObject.unitId || "null",
            partnerUserId: dealObject.partnerName
                ? companyUsers.find((user) => user.name === dealObject.partnerName).id
                : "null",
            dealType: dealObject.type || "null",
            dealChannelType: dealObject.dealTypeChannel || "null",
            closingDate: dealObject.kapanmaTarihi || "null",
            dealCampaignType: "E-Learning",
            activityType: activityType,
            courseName: dealObject.courseName || "null",
            isFromExtension: 1,
            vendorPaymentAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.vendorPaymentAmount : "",
            leoronMarginAmount: dealObject.dealCampaignType === "L&D Broker" ? dealObject.leoronMarginAmount : "",
            endUser: dealObject.type === "Re-Seller Partnership" ? dealObject.endUser : "",
        };

        const params = new URLSearchParams(baseObj);
        return `/activities?${params.toString()}`;
    };

    useEffect(() => {
        if (state.selectedTab !== "Tasks" && state.selectedTasksId) {
            setState((prevState) => ({
                ...prevState,
                selectedTasksId: "",
            }));
        }
    }, [state.selectedTab, state.selectedTasksId]);

    useEffect(() => {
        const taskRelatedContent = [POPUP_CONTENTS.NEW_TASK, POPUP_CONTENTS.PROFILE_TASKS];
        if (state.popupContent && !taskRelatedContent.includes(state.popupContent) && state.selectedTasksId) {
            setState((prevState) => ({
                ...prevState,
                selectedTasksId: "",
            }));
        }
    }, [state.popupContent, state.selectedTasksId]);

    useEffect(() => {
        if (profileNotes && !profileNotesLoading) {
            setNoteList(profileNotes);
        }
    }, [profileNotes, profileNotesLoading]);

    useEffect(() => {
        if (liveNotes.length > 0) {
            const filteredFeed = liveNotes.filter((item) => item.profil.id === profile?.id);
            if (
                filteredFeed?.length > 0 &&
                !profileNotesLoading &&
                filteredFeed[filteredFeed.length - 1]?.id !== noteList[0]?.id
            ) {
                setNoteList((prevState) => [...filteredFeed, ...prevState]);
            }
            setLiveNotes([]);
        }
    }, [liveNotes, noteList, profileNotesLoading]);

    const addProfileNote = (note) => {
        setNoteList((prevState) => [note, ...prevState]);
    };

    useEffect(() => {
        if (!isGenerateNewLead && linkedinUrl && (error?.response.status === 400 || error?.response.status === 404)) {
            setPopit(true);
            setProfile(null);
            setNoteList([]);
            setState(initialState);
        } else if (
            !isGenerateNewLead &&
            (isProfileNotFound ||
                error?.response.status === 500 ||
                error?.response.status === 400 ||
                error?.response.status === 404)
        ) {
            setIsProfileNotFound(true);
            setState((prevState) => ({
                ...prevState,
                selectedTab: "Feed",
                options: [{ value: "Feed" }, { value: "Profile", disabled: true }, { value: "Tasks" }],
            }));
        } else if (!error) {
            setPopit(false);
        }
    }, [error, isProfileNotFound, isGenerateNewLead, linkedinUrl, profileIdParam]);

    const openMailModal = (content) => {
        setState((prevState) => ({
            ...prevState,
            showMailModal: true,
            mailContent: content,
        }));
    };

    const closeMailModal = () => {
        setState((prevState) => ({
            ...prevState,
            showMailModal: false,
            mailContent: null,
        }));
    };

    const setSelectedDeal = (deal) => {
        setState((prevState) => ({
            ...prevState,
            selectedDeal: deal,
        }));
    };

    const setIsDealEditing = (isEditing) => {
        setState((prevState) => ({
            ...prevState,
            isDealEditing: isEditing,
        }));
    };

    const setSelectedDealWithId = (dealId) => {
        const deal = profile?.olanak.find((deal) => deal.id === dealId);
        setState((prevState) => ({
            ...prevState,
            selectedDeal: deal,
        }));
    };

    const handleBulkDelete = async (selectedTasks, bulkDeleteTasks, onSuccess) => {
        try {
            // Send API request to delete selected tasks
            await bulkDeleteTasks(selectedTasks);

            // Call success callback to update local state
            if (onSuccess) {
                onSuccess();
            }

            toast.success(`${selectedTasks.length} task(s) deleted successfully`);

            // Refetch task list to ensure consistency
            fetchStaticTaskList();
        } catch (error) {
            toast.error("Failed to delete tasks");
        }
    };

    const setCollapsedCard = (card, value) => {
        setCollapsedCards((prev) => {
            const updated = { ...prev, [card]: value };
            localStorage.setItem(COLLAPSED_CARDS_KEY, JSON.stringify(updated));
            return updated;
        });
    };

    const value = {
        ...state,
        profile,
        setProfile,
        noteList,
        setNoteList,
        handleDeleteNote,
        isDeletingNote,
        urlParams,
        isProfileLoading: loading,
        setState: (updater) => {
            setState((prevState) => {
                const nextState = typeof updater === "function" ? updater(prevState) : updater;

                return {
                    ...prevState,
                    ...nextState,
                    selectedTab: Object.prototype.hasOwnProperty.call(nextState, "selectedTab")
                        ? nextState.selectedTab
                        : prevState.selectedTab,
                    selectedDate: Object.prototype.hasOwnProperty.call(nextState, "selectedDate")
                        ? nextState.selectedDate
                        : prevState.selectedDate,
                };
            });
        },
        collapsedCards,
        setCollapsedCard,
        refetchProfile: refetch,
        handleUpdateProfile,
        updateProfileLoading,
        updateProfileError,
        profileNotes,
        profileNotesLoading,
        profileNotesRefetch,
        addProfileNote,
        companyUsers,
        companyGroups,
        companyUsersAndGroups,
        dealObject,
        setDealObject,
        handleCreateDeal,
        handleCreateInquiry,
        userActivities,
        fetchUserExtensionActivitiesFunc,
        taskList,
        taskListLoading,
        fetchTaskLists,
        staticTaskList,
        staticTaskListLoading,
        fetchStaticTaskList,
        onTabChange: extensionEvents?.onTabChange,
        onPopupOpen: extensionEvents?.onPopupOpen,
        onPopupClose: extensionEvents?.onPopupClose,
        onProfileLoad: extensionEvents?.onProfileLoad,
        onDealLoad: extensionEvents?.onDealLoad,
        onError: extensionEvents?.onError,
        modals: MODALS,
        isProfileNotFound,
        selectedCampaignStartDate,
        setSelectedCampaignStartDate,
        openMailModal,
        closeMailModal,
        popit,
        setPopit,
        profileIdParam,
        getEsListe,
        profilListe,
        profilListeLoading,
        newLeadObject,
        setNewLeadObject,
        queryProfileId,
        handleDeleteDeal,
        setSelectedDeal,
        olanakEkleLoading,
        setIsDealEditing,
        initialDealObject,
        setSelectedDealWithId,
        createInHouseActivityURL,
        createRegisterInHouseActivityURL,
        createElearningInquiryURL,
        createRegisterELearningActivityURL,
        createActivityURL,
        feed,
        setFeed,
        liveActivities,
        setLiveActivities,
        liveTasks,
        setLiveTasks,
        wonDeals,
        wonDealsLoading,
        wonDealsError,
        fetchWonDealsByAccountId,
        isRedirectingToDeal: state.isRedirectingToDeal,
        isInhouseTypes,
        isInquiryTypes,
        handleBulkDelete,
        refetchEtiketList,
        refetchDeals,
        etiketListLoading,
        etiketListError,
        dealsLoading,
        dealsError,
        refetchTaskList,
        getTaskListLoading,
        taskListError,
        refetchAttachments,
        getAttachmentsLoading,
        attachmentsError,
        resAttachments,
        refetchTags,
        getTagsLoading,
        tagsError,
        resTags,
        profileNotesLoading,
        // Intersection Observer refs for lazy loading
        dealRef,
        courseRef,
        taskRef,
        noteRef,
        typeTagRef,
        dealAttachments,
        setDealAttachments,
        cvAttachment,
        setCvAttachment,
        validateCipdRequiredFields,
        uploadDealCv,
    };

    return <ExtensionContext.Provider value={value}>{children}</ExtensionContext.Provider>;
}
